/**
 * Authentication Utilities
 * Handles login/logout state management and cache clearing
 */

/**
 * Clear all user-related data from localStorage
 * Call this on logout to prevent data leakage between accounts
 */
export const clearAllUserData = () => {
  // Core auth data
  localStorage.removeItem('user');
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  
  // Cached data that might contain user-specific info
  localStorage.removeItem('dashboardStats');
  localStorage.removeItem('recentOrders');
  localStorage.removeItem('franchiseData');
  localStorage.removeItem('staffData');
  localStorage.removeItem('inventoryData');
  localStorage.removeItem('ledgerData');
  
  // Tour/onboarding data (user-specific)
  localStorage.removeItem('tour-completed');
  localStorage.removeItem('tour-skipped');
  
  // Session storage also needs clearing
  sessionStorage.clear();
  
  console.log('[Auth] All user data cleared from storage');
};

/**
 * Full logout - clears all data and optionally clears browser cache
 * @param {Function} setUser - React state setter for user
 * @param {Function} navigate - React Router navigate function
 * @param {string} redirectTo - Path to redirect after logout (default: '/')
 */
export const performLogout = (setUser, navigate, redirectTo = '/') => {
  // Clear all localStorage data
  clearAllUserData();
  
  // Reset React state
  if (setUser) {
    setUser(null);
  }
  
  // Force reload to clear any in-memory state/cache
  // This ensures React Query cache, component state, etc. are all reset
  if (navigate) {
    // Use window.location for a full page reload instead of React Router
    // This ensures all component state is reset
    window.location.href = redirectTo;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const user = localStorage.getItem('user');
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return !!(user && token);
};

/**
 * Get current user from localStorage
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

/**
 * Get auth token
 * @returns {string|null}
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
};
